import { toRaw } from "vue";
import { useApi } from "~/composables/useApi";
import { useAuthStore } from "~/stores/useAuthStore";
import { useInventoryStore, MAX_LEVEL } from "~/stores/useInventoryStore";
import { useDaycareStore } from "~/stores/useDaycareStore";
import {
  BASE_DAYCARE_SLOTS,
  MAX_EXTRA_DAYCARE_SLOTS,
} from "~/stores/useDaycareStore";
import { usePlayerStore } from "~/stores/usePlayerStore";
import { isGameTabLeader } from "~/composables/useMultiTabGuard";
import {
  applyServerDaycareToStore,
  isServerDaycarePayloadEmpty,
  type ServerDaycarePayload,
} from "~/utils/daycareHydrate";
import {
  buildDaycareProgressBackup,
  clearDaycareProgressBackup,
  loadDaycareProgressBackup,
  persistDaycareProgressBackup,
} from "~/utils/daycareProgressBackup";
import { setLastUserIdForLocalBackup } from "~/utils/daycareReserveBackup";
import {
  markDaycareSyncError,
  markDaycareSyncSuccess,
  setDaycareSyncing,
} from "~/composables/useGameSyncStatus";
import {
  captureDaycareMidFlightSnapshot,
  daycareSpeciesKey,
  diffDaycareMidFlightAdditions,
  diffDaycareMidFlightRemovals,
  type DaycareMidFlightSnapshot,
} from "../../../shared/daycare_merge";
import type { DaycareSlot } from "~/stores/useDaycareStore";
import type { DaycareQueueEntry } from "~/utils/daycare";
import { withSuppressedShinyObtainedCelebration } from "~/composables/useCelebrationBanners";

const DAYCARE_SYNC_THROTTLE_MS = 1_200;
const BACKUP_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

let syncInFlight: Promise<DaycareSyncStatus> | null = null;
let syncQueued = false;
/**
 * POST /daycare/hatch en vol. Les PUT /daycare attendent sa fin : un PUT parti pendant
 * l'éclosion embarquait l'œuf déjà éclos côté serveur et pouvait le « ressusciter »
 * (éclosion en double → étoile en plus, Pokémon remplacés par la file d'attente).
 */
let hatchInFlight: Promise<void> | null = null;
let lastSyncAt = 0;
let throttleTimer: ReturnType<typeof setTimeout> | null = null;
/** Évite les PUT en boucle après DAYCARE_LEVEL_GATE (sync périodique game.vue). */
let daycareLevelGateBlocked = false;

/** Aligné serveur `userHasLevel100Pokemon` / client `useFeatureGates.daycareUnlocked`. */
export function clientMeetsDaycareLevelGate(): boolean {
  const inventory = useInventoryStore();
  return inventory.collection.some((p) => p.level >= MAX_LEVEL);
}

export function resetDaycareLevelGateBlock() {
  daycareLevelGateBlocked = false;
}

function daycareMaxSlots() {
  const player = usePlayerStore();
  return (
    BASE_DAYCARE_SLOTS +
    Math.min(MAX_EXTRA_DAYCARE_SLOTS, player.extraDaycareSlots ?? 0)
  );
}

function toSavePlainValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(toRaw(value))) as T;
}

export type SaveDaycareResponse = {
  revision: number;
  daycare: ServerDaycarePayload;
};

export type SaveDaycareConflict = {
  code: "DAYCARE_REVISION_CONFLICT";
  revision: number;
  daycare: ServerDaycarePayload;
};

export type DaycareSyncStatus = "ok" | "conflict" | "blocked" | "error";

/** Persiste le backup local (pagehide / avant sync). */
export function persistDaycareLocalBackup() {
  const auth = useAuthStore();
  const userId = auth.user?.id;
  if (!userId) return;
  setLastUserIdForLocalBackup(userId);
  const daycare = useDaycareStore();
  persistDaycareProgressBackup(
    userId,
    buildDaycareProgressBackup({
      slots: daycare.slots,
      lastDeposited: daycare.lastDeposited,
      reserveQueue: daycare.reserveQueue,
      reserveFillPending: daycare.reserveFillPending,
    }),
  );
}

/**
 * Après load : si le serveur est vide mais un backup récent existe, restaure puis sync.
 * Retourne true si une resync serveur a été déclenchée.
 */
export function tryRestoreDaycareFromPagehideBackup(
  userId: number,
  serverDaycare: unknown,
): boolean {
  if (!isServerDaycarePayloadEmpty(serverDaycare as ServerDaycarePayload))
    return false;
  const player = usePlayerStore();
  if (
    player.ascensionLevel > 0 &&
    (player.currentGeneration ?? 1) <= 1 &&
    (player.badges ?? 0) <= 0
  ) {
    clearDaycareProgressBackup(userId);
    return false;
  }
  if (!clientMeetsDaycareLevelGate()) {
    clearDaycareProgressBackup(userId);
    return false;
  }
  const backup = loadDaycareProgressBackup(userId);
  if (!backup) return false;
  const age = Date.now() - (backup.ts || 0);
  if (age > BACKUP_MAX_AGE_MS) return false;
  const hasContent =
    backup.slots.length > 0 ||
    backup.reserveQueue.length > 0 ||
    backup.lastDeposited.length > 0;
  if (!hasContent) return false;

  const daycare = useDaycareStore();
  daycare.setSlots(backup.slots);
  daycare.setLastDeposited(backup.lastDeposited);
  daycare.setReserveQueue(backup.reserveQueue);
  daycare.setReserveFillPending(backup.reserveFillPending);
  daycare.noteDaycareMutation();
  void syncDaycareToServer(true);
  return true;
}

/** Hydrate le store depuis la réponse /load ou /daycare. */
export function hydrateDaycareFromServerPayload(serverDaycare: unknown) {
  // Rechargement complet (login / reload) : les dépôts en attente n'ont plus de sens.
  resetDaycarePendingMutations();
  applyServerDaycareToStore(
    serverDaycare as ServerDaycarePayload,
    daycareMaxSlots(),
  );
  persistDaycareLocalBackup();
}

type DaycareMidFlightAdditions = {
  addedSlots: DaycareSlot[];
  addedReserve: DaycareQueueEntry[];
};

/**
 * Dépôts pas encore confirmés par le serveur : faits pendant qu’une requête pension
 * (PUT /daycare ou POST /daycare/hatch) était en vol. Sans ce registre, l’application de
 * la réponse serveur écrase le store et « dégage » le Pokémon tout juste déposé.
 * Purgé quand un PUT réussi les a inclus, abandonné si le joueur les retire localement.
 */
let daycarePendingAdditions: DaycareMidFlightAdditions = {
  addedSlots: [],
  addedReserve: [],
};

/**
 * Miroir du registre de dépôts : espèces retirées de la pension pendant qu’une requête
 * était en vol. Sans lui, la réponse serveur « ressuscite » le Pokémon retiré.
 * Purgé quand un PUT réussi est parti sans l’espèce, abandonné si elle est re-déposée.
 */
let daycarePendingRemovals: Set<string> = new Set();

export function resetDaycarePendingMutations() {
  daycarePendingAdditions = { addedSlots: [], addedReserve: [] };
  daycarePendingRemovals = new Set();
}

function daycarePendingMutationsCount(): number {
  return (
    daycarePendingAdditions.addedSlots.length +
    daycarePendingAdditions.addedReserve.length +
    daycarePendingRemovals.size
  );
}

function captureDaycareMidFlightState(): DaycareMidFlightSnapshot {
  const daycare = useDaycareStore();
  return captureDaycareMidFlightSnapshot(daycare.slots, daycare.reserveQueue);
}

/**
 * Met à jour le registre des dépôts en attente, juste AVANT `applyServerDaycareToStore`
 * (qui écrase l’état local). Ajoute les dépôts apparus pendant la requête ; retire ceux
 * que le joueur a enlevés localement ; avec `purgeSentEntries` (PUT réussi), retire ceux
 * qui étaient dans le payload envoyé — le serveur les connaît désormais.
 */
function refreshDaycarePendingAdditions(
  snapshot: DaycareMidFlightSnapshot,
  options: { purgeSentEntries: boolean },
): DaycareMidFlightAdditions {
  const daycare = useDaycareStore();
  const localKeys = new Set<string>([
    ...daycare.slots.map((s) => daycareSpeciesKey(s)),
    ...daycare.reserveQueue.map((e) => daycareSpeciesKey(e)),
  ]);
  const sentKeys = options.purgeSentEntries
    ? new Set<string>([...snapshot.slotKeys, ...snapshot.reserveKeys])
    : new Set<string>();

  const { addedSlots, addedReserve } = diffDaycareMidFlightAdditions(
    snapshot,
    daycare.slots,
    daycare.reserveQueue,
  );

  const seen = new Set<string>();
  const nextSlots: DaycareSlot[] = [];
  const nextReserve: DaycareQueueEntry[] = [];
  const pushSlot = (slot: DaycareSlot) => {
    const key = daycareSpeciesKey(slot);
    if (seen.has(key)) return;
    seen.add(key);
    nextSlots.push(toSavePlainValue(slot));
  };
  const pushReserve = (entry: DaycareQueueEntry) => {
    const key = daycareSpeciesKey(entry);
    if (seen.has(key)) return;
    seen.add(key);
    nextReserve.push(toSavePlainValue(entry));
  };

  for (const slot of addedSlots) pushSlot(slot);
  for (const entry of addedReserve) pushReserve(entry);
  const keepPending = (key: string) => localKeys.has(key) && !sentKeys.has(key);
  for (const slot of daycarePendingAdditions.addedSlots) {
    if (keepPending(daycareSpeciesKey(slot))) pushSlot(slot);
  }
  for (const entry of daycarePendingAdditions.addedReserve) {
    if (keepPending(daycareSpeciesKey(entry))) pushReserve(entry);
  }

  daycarePendingAdditions = { addedSlots: nextSlots, addedReserve: nextReserve };
  return daycarePendingAdditions;
}

/**
 * Pendant du refresh des dépôts, pour les retraits. À appeler AVANT l’écrasement du store.
 * - nouveau retrait détecté : espèce du snapshot absente de l’état local ;
 * - espèce re-déposée localement : retrait abandonné (dépôt volontaire, ex. loop refill) ;
 * - `purgeSentEntries` (PUT réussi) : un payload parti SANS l’espèce a déjà transmis le
 *   retrait au serveur (le PUT remplace l’état, il ne fusionne pas) — plus rien à re-faire.
 */
function refreshDaycarePendingRemovals(
  snapshot: DaycareMidFlightSnapshot,
  options: { purgeSentEntries: boolean },
): Set<string> {
  const daycare = useDaycareStore();
  const localKeys = new Set<string>([
    ...daycare.slots.map((s) => daycareSpeciesKey(s)),
    ...daycare.reserveQueue.map((e) => daycareSpeciesKey(e)),
  ]);
  const sentKeys = options.purgeSentEntries
    ? new Set<string>([...snapshot.slotKeys, ...snapshot.reserveKeys])
    : null;

  const next = diffDaycareMidFlightRemovals(
    snapshot,
    daycare.slots,
    daycare.reserveQueue,
  );
  for (const key of daycarePendingRemovals) {
    if (localKeys.has(key)) continue;
    if (sentKeys && !sentKeys.has(key)) continue;
    next.add(key);
  }
  daycarePendingRemovals = next;
  return next;
}

/**
 * Re-retire du store les espèces en attente de retrait, APRÈS écrasement par la réponse
 * serveur (sinon le Pokémon retiré « ressuscite »). Retourne true si mutation appliquée.
 */
function reapplyDaycarePendingRemovals(removals: ReadonlySet<string>): boolean {
  if (removals.size === 0) return false;
  const daycare = useDaycareStore();
  let changed = false;
  const keptSlots = daycare.slots.filter(
    (s) => !removals.has(daycareSpeciesKey(s)),
  );
  if (keptSlots.length !== daycare.slots.length) {
    daycare.setSlots(keptSlots);
    // Même garde-fou qu'un retrait manuel (cf. removeSlot) : sans lui, le remplissage
    // passif re-remplirait aussitôt le slot libéré avec le même candidat.
    daycare.setPassiveVacancyRefillSuppressed(true);
    changed = true;
  }
  const keptReserve = daycare.reserveQueue.filter(
    (e) => !removals.has(daycareSpeciesKey(e)),
  );
  if (keptReserve.length !== daycare.reserveQueue.length) {
    daycare.setReserveQueue(keptReserve);
    changed = true;
  }
  if (changed) {
    daycare.noteDaycareMutation();
    daycare.flushDaycareProgressBackup();
  }
  return changed;
}

function daycareSlotToReserveEntry(slot: DaycareSlot): DaycareQueueEntry {
  return {
    slug: slot.slug,
    nameFr: slot.nameFr,
    nameEn: slot.nameEn,
    rarity: slot.rarity,
    isShiny: slot.isShiny,
    stars: slot.stars,
    cosmeticFormSlug: slot.cosmeticFormSlug ?? null,
  };
}

/**
 * Re-pose dans le store les dépôts en attente, APRÈS écrasement par la réponse serveur.
 * Slots sans place libre → file de réserve. Retourne true si quelque chose a été re-posé.
 */
function reapplyDaycarePendingAdditions(
  additions: DaycareMidFlightAdditions,
  maxSlots: number,
): boolean {
  const daycare = useDaycareStore();
  let changed = false;
  const overflow: DaycareQueueEntry[] = [];
  for (const slot of additions.addedSlots) {
    if (daycare.hasSlug(slot.slug, slot.isShiny)) continue;
    if (daycare.slots.length < maxSlots) {
      daycare.setSlots([...daycare.slots, { ...slot }]);
      daycare.dequeueReserveBySlug(slot.slug, slot.isShiny);
      changed = true;
    } else {
      overflow.push(daycareSlotToReserveEntry(slot));
    }
  }
  const reserveAdditions = [...overflow, ...additions.addedReserve].filter(
    (entry) => !daycare.hasSlug(entry.slug, entry.isShiny),
  );
  if (
    reserveAdditions.length > 0 &&
    daycare.enqueueReserve(reserveAdditions) > 0
  ) {
    changed = true;
  }
  if (changed) {
    daycare.noteDaycareMutation();
    daycare.flushDaycareProgressBackup();
  }
  return changed;
}

/**
 * Séquence complète autour d’une réponse serveur qui écrase le store :
 * registres à jour → apply → re-pose des dépôts + re-retrait des retraits →
 * re-sync tant que des mutations restent en attente serveur.
 * Les deux registres sont mutuellement exclusifs par espèce (les refresh s’appuient sur
 * le même état local pré-apply : présente ⇒ jamais en retrait, absente ⇒ jamais en dépôt).
 */
function applyServerDaycarePreservingPendingMutations(
  serverDaycare: ServerDaycarePayload,
  snapshot: DaycareMidFlightSnapshot,
  options: { purgeSentEntries: boolean; revision?: number },
): void {
  const pendingAdditions = refreshDaycarePendingAdditions(snapshot, {
    purgeSentEntries: options.purgeSentEntries,
  });
  const pendingRemovals = refreshDaycarePendingRemovals(snapshot, {
    purgeSentEntries: options.purgeSentEntries,
  });
  applyServerDaycareToStore(serverDaycare, daycareMaxSlots());
  if (typeof options.revision === "number") {
    const daycare = useDaycareStore();
    daycare.setServerRevision(options.revision);
    daycare.clearPendingRevision();
  }
  // Après clearPendingRevision : le noteDaycareMutation du re-pose doit survivre.
  reapplyDaycarePendingAdditions(pendingAdditions, daycareMaxSlots());
  reapplyDaycarePendingRemovals(pendingRemovals);
  if (daycarePendingMutationsCount() > 0) {
    void syncDaycareToServer(false);
  }
}

function buildDaycareSyncPayload(revision: number) {
  const daycare = useDaycareStore();
  return {
    revision,
    baseRevision: daycare.serverRevision,
    slots: toSavePlainValue(daycare.slots),
    autoRefill: daycare.autoRefill,
    autoVacancyFill: daycare.autoVacancyFill,
    lastDeposited: toSavePlainValue(daycare.lastDeposited),
    reserveQueue: toSavePlainValue(daycare.reserveQueue),
    reserveFillPending: daycare.reserveFillPending,
  };
}

/** Payload PUT /daycare pour keepalive pagehide — null si rien à synchroniser. */
export function buildDaycareSyncKeepalivePayload(): Record<
  string,
  unknown
> | null {
  const auth = useAuthStore();
  if (!auth.isAuthenticated || !auth.gameLoaded || !auth.sessionToken)
    return null;
  if (!isGameTabLeader()) return null;

  const daycare = useDaycareStore();
  if (daycare.slots.length <= 0) return null;
  if (!clientMeetsDaycareLevelGate()) return null;

  return {
    ...buildDaycareSyncPayload(daycare.revisionForSave()),
    sessionToken: auth.sessionToken,
    adminVersion: usePlayerStore().adminVersion,
  };
}

/**
 * Fermeture d'onglet : sync pension serveur en keepalive (fire-and-forget).
 * Améliore la précision de la simulation d'œufs offline au prochain login.
 */
export function beaconDaycareSyncOnPageHide(): void {
  const payload = buildDaycareSyncKeepalivePayload();
  if (!payload) return;
  try {
    void useApi()
      .put("/api/game/daycare", payload, { keepalive: true })
      .catch(() => {
        // Discret : la page se ferme, pas de gestion de conflit révision.
      });
  } catch {
    /* garde-fou synchrone */
  }
}

async function runDaycareSync(force: boolean): Promise<DaycareSyncStatus> {
  const auth = useAuthStore();
  if (!auth.isAuthenticated || !auth.gameLoaded) return "error";
  if (!force && !isGameTabLeader()) return "error";
  if (!clientMeetsDaycareLevelGate()) {
    if (force) {
      const userId = auth.user?.id;
      if (userId) clearDaycareProgressBackup(userId);
      markDaycareSyncError(
        "Un pokemon niveau 100 est requis pour deposer en pension.",
      );
    }
    return "blocked";
  }
  if (!force && daycareLevelGateBlocked) return "blocked";

  await auth.resyncSessionTokenIfMissing();
  if (!auth.sessionToken) return "error";

  // Payload et révision calculés APRÈS l'éclosion en vol (état + révision serveur à jour).
  while (hatchInFlight) {
    await hatchInFlight.catch(() => undefined);
  }

  const daycare = useDaycareStore();
  const revision = daycare.revisionForSave();
  persistDaycareLocalBackup();
  setDaycareSyncing(true);

  // Un dépôt effectué pendant que le PUT est en vol n’est pas dans le payload : sans ce
  // snapshot, l’application de la réponse serveur le retirerait aussitôt du store.
  const midFlightSnapshot = captureDaycareMidFlightState();

  const api = useApi();
  try {
    const res = await api.put<SaveDaycareResponse>("/api/game/daycare", {
      ...buildDaycareSyncPayload(revision),
      sessionToken: auth.sessionToken,
      adminVersion: usePlayerStore().adminVersion,
    });
    daycareLevelGateBlocked = false;
    applyServerDaycarePreservingPendingMutations(res.daycare, midFlightSnapshot, {
      purgeSentEntries: true,
      revision: res.revision,
    });
    persistDaycareLocalBackup();
    markDaycareSyncSuccess();
    return "ok";
  } catch (e: unknown) {
    const err = e as {
      status?: number;
      message?: string;
      data?: Omit<SaveDaycareConflict, "code"> & {
        code?: string;
        revision?: number;
        daycare?: ServerDaycarePayload;
      };
    };
    const conflict = err?.data as SaveDaycareConflict | undefined;
    if (err?.status === 409 && conflict?.code === "DAYCARE_REVISION_CONFLICT") {
      daycareLevelGateBlocked = false;
      // Payload rejeté (pas de purge) : les dépôts en vol restent à re-poser puis re-sync.
      applyServerDaycarePreservingPendingMutations(
        conflict.daycare,
        midFlightSnapshot,
        { purgeSentEntries: false, revision: conflict.revision },
      );
      persistDaycareLocalBackup();
      markDaycareSyncSuccess();
      return "conflict";
    }
    if (err?.status === 422 && err?.data?.code === "DAYCARE_LEVEL_GATE") {
      daycareLevelGateBlocked = true;
      const userId = auth.user?.id;
      if (userId) clearDaycareProgressBackup(userId);
      // Ne pas réappliquer l’état serveur (souvent vide) : ça effaçait tous les slots côté UI.
      markDaycareSyncError(
        "Un pokemon niveau 100 est requis pour deposer en pension.",
      );
      return "blocked";
    }
    console.warn("[daycare] sync échouée :", e);
    markDaycareSyncError(err?.message ?? "Echec sync pension");
    return "error";
  } finally {
    setDaycareSyncing(false);
  }
}

/**
 * Sync pension dédiée (révision monotone). Throttle léger sauf `force`.
 * Les dégâts d’œufs sont inclus dans le payload ; pas de bump de révision à chaque tick de dégâts.
 */
export async function syncDaycareToServer(
  force = false,
): Promise<DaycareSyncStatus> {
  const auth = useAuthStore();
  if (!auth.isAuthenticated || !auth.gameLoaded) return "error";
  if (!force && !isGameTabLeader()) return "error";

  if (!force) {
    const elapsed = Date.now() - lastSyncAt;
    if (elapsed < DAYCARE_SYNC_THROTTLE_MS) {
      if (!throttleTimer) {
        throttleTimer = setTimeout(() => {
          throttleTimer = null;
          void syncDaycareToServer(false);
        }, DAYCARE_SYNC_THROTTLE_MS - elapsed);
      }
      return "error";
    }
  }

  if (syncInFlight) {
    syncQueued = true;
    return syncInFlight;
  }

  syncInFlight = (async () => {
    const status = await runDaycareSync(force);
    lastSyncAt = Date.now();
    syncInFlight = null;
    if (syncQueued) {
      syncQueued = false;
      void syncDaycareToServer(false);
    }
    return status;
  })();

  return await syncInFlight;
}

/** Après éclosion : sync pension + sauvegarde Pokémon si besoin. */
export async function saveAfterDaycareHatch() {
  persistDaycareLocalBackup();
  const auth = useAuthStore();
  if (!auth.gameLoaded) return;
  if (!isGameTabLeader()) {
    persistDaycareLocalBackup();
    return;
  }
  await syncDaycareToServer(true);
  // Ne pas bloquer l’auto-refill pension sur un save complet (contention avec credit-gold).
  void auth.saveGameState(false, true, {
    pokemonsFirst: true,
    forcePokemons: true,
  });
}

export type ServerDaycareHatch = {
  slug: string;
  nameFr: string;
  nameEn: string;
  rarity: string;
  isShiny: boolean;
  stars: number;
  cosmeticFormSlug: string | null;
  loopSameSpecies: boolean;
  parentStars: number;
  parentIsShiny?: boolean;
  serverId: number | null;
  level: number;
  xp: number;
  teamSlot: number | null;
  isFavorite: boolean;
  starUpgraded: boolean;
  alreadyMaxed: boolean;
  configuredRarity: string;
};

function applyHatchRewardToInventory(h: ServerDaycareHatch) {
  const inventory = useInventoryStore();
  const serverId =
    typeof h.serverId === "number" && h.serverId > 0 ? h.serverId : null;
  const existing =
    (serverId != null
      ? inventory.collection.find((p) => p.serverId === serverId)
      : undefined) ??
    inventory.collection.find(
      (p) => p.slug === h.slug && p.isShiny === h.isShiny,
    );

  if (existing) {
    if (serverId != null) existing.serverId = serverId;
    existing.stars = Math.max(1, Math.min(5, Math.floor(h.stars)));
    existing.isShiny = h.isShiny;
    existing.rarity = (h.configuredRarity ||
      h.rarity ||
      existing.rarity) as typeof existing.rarity;
    if (typeof h.level === "number") existing.level = h.level;
    if (typeof h.xp === "number") existing.xp = h.xp;
    if ("teamSlot" in h) existing.teamSlot = h.teamSlot ?? null;
    if (typeof h.isFavorite === "boolean") existing.isFavorite = h.isFavorite;
    if (h.cosmeticFormSlug) existing.cosmeticFormSlug = h.cosmeticFormSlug;
    return { isNew: false, pokemon: existing };
  }

  if (serverId == null) {
    // Fallback local si le grant serveur n’a pas renvoyé d’id (ne doit pas arriver).
    return inventory.addPokemon({
      slug: h.slug,
      nameFr: h.nameFr,
      nameEn: h.nameEn,
      stars: 1,
      isShiny: false,
      rarity: (h.configuredRarity || h.rarity || "common") as "common",
      cosmeticFormSlug: h.cosmeticFormSlug,
    });
  }

  const pokemon = {
    id: inventory.nextId++,
    serverId,
    slug: h.slug,
    nameFr: h.nameFr,
    nameEn: h.nameEn,
    level: h.level ?? 1,
    xp: h.xp ?? 0,
    stars: Math.max(1, Math.min(5, Math.floor(h.stars))),
    isShiny: h.isShiny,
    rarity: (h.configuredRarity || h.rarity || "common") as "common",
    teamSlot: h.teamSlot ?? null,
    isFavorite: h.isFavorite === true,
    cosmeticFormSlug: h.cosmeticFormSlug ?? null,
  };
  inventory.collection.push(pokemon);
  return { isNew: true, pokemon };
}

/**
 * Éclosion pension 100 % serveur (RNG shiny + grant).
 * Remplace le flux client isShinyResult + save-pokemons.
 */
export async function hatchDaycareOnServer(options?: {
  indexes?: number[];
}): Promise<{
  hatched: Array<{
    slot: {
      slug: string;
      nameFr: string;
      nameEn: string;
      rarity: string;
      isShiny: boolean;
      stars: number;
      cosmeticFormSlug: string | null;
      loopSameSpecies: boolean;
    };
    isShiny: boolean;
    isNew: boolean;
    stars: number;
  }>;
} | null> {
  const auth = useAuthStore();
  const player = usePlayerStore();
  if (!auth.isAuthenticated || !auth.gameLoaded) return null;
  if (!isGameTabLeader()) return null;

  await auth.resyncSessionTokenIfMissing();
  if (!auth.sessionToken) return null;

  // Persister les dégâts avant hatch (œufs « prêts » côté serveur).
  // Si la sync échoue (429 / erreur), ne pas enchaîner hatch → boucle vide + rate-limit.
  const syncStatus = await syncDaycareToServer(true);
  if (syncStatus === "error" || syncStatus === "blocked") {
    return null;
  }

  // Une seule requête pension à la fois : pas de hatch pendant un PUT (ou un autre hatch).
  while (syncInFlight || hatchInFlight) {
    await (syncInFlight ?? hatchInFlight)?.catch(() => undefined);
  }

  // Même protection que la sync PUT : un dépôt fait pendant le POST hatch (fenêtre du
  // palier 1M atteint) ne doit pas être dégagé quand la réponse écrase le store.
  const midFlightSnapshot = captureDaycareMidFlightState();

  let releaseHatch!: () => void;
  const hatchDone = new Promise<void>((resolve) => {
    releaseHatch = resolve;
  });
  hatchInFlight = hatchDone;

  const api = useApi();
  try {
    const res = await api.post<{
      hatched?: ServerDaycareHatch[];
      daycare?: ServerDaycarePayload;
      adminVersion?: number;
      reload?: boolean;
    }>("/api/game/daycare/hatch", {
      indexes: options?.indexes,
      sessionToken: auth.sessionToken,
      adminVersion: player.adminVersion,
    });

    if (res?.reload) {
      await auth.loadGameState();
      return null;
    }

    if (res?.daycare) {
      // Pas de purge : seul un PUT réussi transmet les dépôts au serveur.
      applyServerDaycarePreservingPendingMutations(
        res.daycare,
        midFlightSnapshot,
        { purgeSentEntries: false },
      );
    }
    if (typeof res?.adminVersion === "number") {
      player.adminVersion = res.adminVersion;
    }

    const hatched: Array<{
      slot: {
        slug: string;
        nameFr: string;
        nameEn: string;
        rarity: string;
        isShiny: boolean;
        stars: number;
        cosmeticFormSlug: string | null;
        loopSameSpecies: boolean;
      };
      isShiny: boolean;
      isNew: boolean;
      stars: number;
    }> = [];

    withSuppressedShinyObtainedCelebration(() => {
      for (const h of res?.hatched ?? []) {
        const { isNew, pokemon } = applyHatchRewardToInventory(h);
        hatched.push({
          slot: {
            slug: h.slug,
            nameFr: h.nameFr,
            nameEn: h.nameEn,
            rarity: h.rarity,
            // Parent déposé (pour loop refill), pas le résultat du roll shiny.
            isShiny: h.parentIsShiny === true,
            stars: h.parentStars ?? 1,
            cosmeticFormSlug: h.cosmeticFormSlug,
            loopSameSpecies: h.loopSameSpecies !== false,
          },
          isShiny: h.isShiny === true,
          isNew,
          stars: pokemon.stars,
        });
      }
    });

    if (hatched.length > 0) {
      player.addStat("daycareHatches", hatched.length);
      persistDaycareLocalBackup();
    }

    return { hatched };
  } catch (e: unknown) {
    console.warn("[DAYCARE] Server hatch failed:", e);
    return null;
  } finally {
    if (hatchInFlight === hatchDone) hatchInFlight = null;
    releaseHatch();
  }
}

export function flushDaycareSyncNow() {
  if (throttleTimer) {
    clearTimeout(throttleTimer);
    throttleTimer = null;
  }
  persistDaycareLocalBackup();
  return syncDaycareToServer(true);
}

/** Avant offline-depart : persiste la pension serveur (slots, dégâts, files). */
export async function flushDaycareBeforeOfflineDepart(): Promise<void> {
  const auth = useAuthStore();
  if (!auth.isAuthenticated || !auth.gameLoaded) return;
  if (!isGameTabLeader()) return;
  try {
    await flushDaycareSyncNow();
  } catch (e: unknown) {
    console.warn("[DAYCARE] Pre-logout sync failed:", e);
  }
}
